import type { QuotaPeriod, RecurrenceEnd, RecurrenceRule, RecurrenceUnit, Weekday } from '../db/models'
import { addDays, dateInMonth, dateKey } from '../date/calendar'

/**
 * **Ein Wiederholungs-Satz → `RecurrenceRule`**, auf Deutsch und Englisch: „every 2 weeks on mon“,
 * „jeden Montag“, „alle! 3 Tage“ (nach Abhaken), „werktags“, „jeden 3. Freitag“, „3x pro Woche“.
 *
 * Entstanden für den Todoist-Import (T10, #175), seit #234 auch für die **Erkennung im Titel** —
 * darum hier und nicht mehr im Import. Was ein Satz nicht nennt, kommt vom **Ankertag**: „every
 * week“ an einem Donnerstag wird „jeden Donnerstag“. Der Import ankert an Todoists `due.date`, der
 * Titel an heute.
 *
 * Ein Ende („until dec 31“, „for 3 weeks“, „bis 31.12.“) wird zuerst abgetrennt und zum `end` der
 * Regel (T13, #178). Alles, was HappyTask nicht kennt, gibt `null` — lieber ehrlich „kann ich nicht“
 * als eine falsche Regel.
 */
export function parseRecurrencePhrase(phrase: string, anchorDay: Date | null): RecurrenceRule | null {
  const normalized = normalize(phrase)
  const split = splitEnd(normalized.text, anchorDay)
  if (!split) return null
  const rule = translateRhythm(split.text, normalized.afterDone, anchorDay)
  return rule && split.end ? { ...rule, end: split.end } : rule
}

/**
 * **Die Quote** (#234): „3x pro Woche“, „3 mal im Monat“, „3-mal pro Jahr“, „3 times a week“,
 * „3x per month“. Welche Tage, entscheidet man selbst — es gibt keinen Termin.
 */
function quotaOf(text: string): RecurrenceRule | null {
  const match = /^(\d+)\s*(?:x|×|-?mal|times?)\s+(?:pro|im|in der|je|a|an|per|each)\s+([a-zä]+)$/.exec(text)
  if (!match) return null
  const count = Number(match[1])
  const period = QUOTA_PERIODS[match[2]!]
  return count >= 1 && period ? { type: 'quota', count, period } : null
}

const QUOTA_PERIODS: Record<string, QuotaPeriod> = {
  woche: 'week', week: 'week', monat: 'month', month: 'month', jahr: 'year', year: 'year',
}

const WEEKDAYS: Record<string, Weekday> = {
  mon: 0, monday: 0, mo: 0, montag: 0,
  tue: 1, tues: 1, tuesday: 1, di: 1, dienstag: 1,
  wed: 2, wednesday: 2, mi: 2, mittwoch: 2,
  thu: 3, thur: 3, thurs: 3, thursday: 3, do: 3, donnerstag: 3,
  fri: 4, friday: 4, fr: 4, freitag: 4,
  sat: 5, saturday: 5, sa: 5, samstag: 5, sonnabend: 5,
  sun: 6, sunday: 6, so: 6, sonntag: 6,
}

const MONTHS: Record<string, number> = {
  jan: 1, january: 1, januar: 1,
  feb: 2, february: 2, februar: 2,
  mar: 3, march: 3, mär: 3, märz: 3, maerz: 3,
  apr: 4, april: 4,
  may: 5, mai: 5,
  jun: 6, june: 6, juni: 6,
  jul: 7, july: 7, juli: 7,
  aug: 8, august: 8,
  sep: 9, sept: 9, september: 9,
  oct: 10, october: 10, okt: 10, oktober: 10,
  nov: 11, november: 11,
  dec: 12, december: 12, dez: 12, dezember: 12,
}

const NUMBER_WORDS: Record<string, number> = {
  other: 2, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10, eleven: 11, twelve: 12,
  zwei: 2, zweite: 2, zweiten: 2, zweiter: 2, zweites: 2, drei: 3, dritte: 3, dritten: 3, vier: 4, fünf: 5, sechs: 6,
  sieben: 7, acht: 8, neun: 9, zehn: 10, elf: 11, zwölf: 12,
}

const UNITS: Record<string, RecurrenceUnit> = {
  day: 'day', days: 'day', tag: 'day', tage: 'day', tagen: 'day',
  week: 'week', weeks: 'week', woche: 'week', wochen: 'week',
  month: 'month', months: 'month', monat: 'month', monate: 'month', monaten: 'month',
  year: 'year', years: 'year', jahr: 'year', jahre: 'year', jahren: 'year',
}

/** Ein Wort, das allein schon die Regel ist: „daily“, „wöchentlich“ … */
const STANDALONE: Record<string, RecurrenceUnit | 'workdays'> = {
  daily: 'day', täglich: 'day',
  weekly: 'week', wöchentlich: 'week',
  monthly: 'month', monatlich: 'month',
  yearly: 'year', annually: 'year', jährlich: 'year',
  werktags: 'workdays',
}

const WORKDAYS: Weekday[] = [0, 1, 2, 3, 4]
const WEEKEND: Weekday[] = [5, 6]

/** Was HappyTask (noch) nicht kennt — hier bricht die Übersetzung ab. */
const UNSUPPORTED = [
  /\b(hours?|minutes?|mins?|stunden?|minuten?|stündlich)\b/, // unter einem Tag
  /\b(until|ending|for \d+|bis|endet)\b/, // ein Ende, das `splitEnd` nicht lesen konnte
  /\b(last|letzte[nrs]?)\b/, // letzter Tag / Wochentag (T12)
  /\b(first|second|third|fourth|erste[nrs]?|vierte[nrs]?)\b/, // n-ter Wochentag (T11)
  /\b\d+(st|nd|rd|th)\s+(\w+)/, // „3rd friday“, aber „15th“ allein ist ein Monatstag
  /\b\d+\.\s*[a-zä]+tag\b/, // „3. Freitag“
  /\b(dritte[nrs]?)\s+[a-zä]+tag\b/,
]

/** Wochentag, Tag und Monat des Ankertags — daraus kommt, was der Satz nicht nennt („every week“ → sein Wochentag). */
function anchorOf(day: Date | null): { weekday: Weekday; day: number; month: number } | null {
  if (!day) return null
  return { weekday: ((day.getDay() + 6) % 7) as Weekday, day: day.getDate(), month: day.getMonth() + 1 }
}

function normalize(text: string): { text: string; afterDone: boolean } {
  let s = text.toLowerCase().trim().replace(/\s+/g, ' ')
  const afterDone = /^(every|jeden|jede|jedes|jeder|alle)!/.test(s)
  s = s.replace(/^(every|jeden|jede|jedes|jeder|alle)!/, '$1')
  // Start und Uhrzeit stecken schon in `due.date`.
  // Start und Uhrzeit fallen weg, ein Ende dahinter bleibt stehen.
  const upToEnd = String.raw`.*?(?=\s+(?:until|ending|bis|endet|for \d|für \d)\b|$)`
  s = s.replace(new RegExp(String.raw`\s+(starting|from|ab|beginnend)\b` + upToEnd), '')
  s = s.replace(new RegExp(String.raw`\s+(at|@|um)\s+\d` + upToEnd), '')
  s = s.replace(new RegExp(String.raw`\s+\d{1,2}(:\d{2})?\s*(am|pm|uhr)\b` + upToEnd), '')
  s = s.replace(/[.,]\s*$/, '')
  return { text: s.trim(), afterDone }
}

/** „mon, thu“ / „montag und donnerstag“ → Wochentage; `null`, wenn ein Wort keiner ist. */
function weekdaysOf(text: string): Weekday[] | null {
  const words = text.split(/\s*(?:,|&|\band\b|\bund\b)\s*|\s+/).filter(Boolean)
  if (words.length === 0) return null
  const days: Weekday[] = []
  for (const word of words) {
    const day = WEEKDAYS[word]
    if (day === undefined) return null
    if (!days.includes(day)) days.push(day)
  }
  return days.sort((a, b) => a - b)
}

/** „jan 27“, „27 jan“, „27. januar“, „january 27th“ → Monat + Tag. */
function monthDayOf(text: string): { month: number; day: number } | null {
  const cleaned = text.replace(/(\d+)(st|nd|rd|th|\.)/g, '$1')
  const a = /^([a-zä]+) (\d{1,2})$/.exec(cleaned)
  const b = /^(\d{1,2}) ([a-zä]+)$/.exec(cleaned)
  const [monthName, dayText] = a ? [a[1]!, a[2]!] : b ? [b[2]!, b[1]!] : [null, null]
  if (!monthName || !dayText) return null
  const month = MONTHS[monthName]
  const day = Number(dayText)
  if (!month || day < 1 || day > 31) return null
  return { month, day }
}

function fixedRule(unit: RecurrenceUnit, interval: number, anchor: ReturnType<typeof anchorOf>): RecurrenceRule | null {
  switch (unit) {
    case 'day': return { type: 'fixed', interval, unit: 'day' }
    case 'week': return anchor ? { type: 'fixed', interval, unit: 'week', weekdays: [anchor.weekday] } : null
    case 'month': return anchor ? { type: 'fixed', interval, unit: 'month', monthDay: anchor.day } : null
    case 'year': return anchor ? { type: 'fixed', interval, unit: 'year', yearMonth: anchor.month, yearDay: anchor.day } : null
  }
}

/** „3rd“, „third“, „3.“, „last“, „letzten“ → n (−1 = letzter). Deutsch nur mit Ziffer oder „letzten“. */
const ORDINALS: Record<string, 1 | 2 | 3 | 4 | -1> = {
  first: 1, '1st': 1, second: 2, '2nd': 2, third: 3, '3rd': 3, fourth: 4, '4th': 4, last: -1,
  '1.': 1, '2.': 2, '3.': 3, '4.': 4, letzten: -1, letzte: -1, letzter: -1, letztes: -1,
}

/**
 * „every 3rd friday“, „every 2 months on the last monday“, „jeden 3. Freitag“,
 * „jeden letzten Tag“ (T11/T12). **Nicht** „jeden zweiten Montag“: Das heißt auf
 * Deutsch „alle zwei Wochen“ und bleibt beim Wochen-Muster unten.
 */
function monthlyOrdinal(text: string, anchor: ReturnType<typeof anchorOf>): RecurrenceRule | null | undefined {
  const match = /^(?:every|jeden|jede|jedes|alle)(?: (\d+|other) (?:months?|monate?n?)(?: on the| on| am)?)? (?:on the |the |am )?(\S+) ([a-zä]+)(?: of the month| im monat| des monats)?$/.exec(text)
  if (!match) return undefined
  const nth = ORDINALS[match[2]!]
  if (nth === undefined) return undefined
  const interval = match[1] === undefined ? 1 : match[1] === 'other' ? 2 : Number(match[1])
  if (!(interval >= 1)) return null
  const fallbackDay = anchor?.day ?? 1
  if (nth === -1 && /^(day|tag)$/.test(match[3]!)) return { type: 'fixed', interval, unit: 'month', monthDay: 31 }
  const weekday = WEEKDAYS[match[3]!]
  if (weekday === undefined) return undefined
  return { type: 'fixed', interval, unit: 'month', monthDay: fallbackDay, nthWeekday: { nth, weekday } }
}

/** „2026-12-31“, „31.12.2026“, „31.12.“, „dec 31“, „31 dec 2026“, „31. dezember“ → Tag; ohne Jahr der nächste ab `start`. */
function endDateOf(text: string, start: Date): Date | null {
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text)
  if (iso) return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]))
  let day: number, month: number, year: number | null = null
  const dotted = /^(\d{1,2})\.(\d{1,2})\.?(\d{4}|\d{2})?$/.exec(text) // „31.12.“ kommt ohne Schlusspunkt an
  if (dotted) {
    day = Number(dotted[1]); month = Number(dotted[2])
    if (dotted[3]) year = dotted[3].length === 2 ? 2000 + Number(dotted[3]) : Number(dotted[3])
  } else {
    const withYear = /^(.+?),? (\d{4})$/.exec(text)
    const named = monthDayOf(withYear ? withYear[1]! : text)
    if (!named) return null
    ;({ day, month } = named)
    if (withYear) year = Number(withYear[2])
  }
  if (month < 1 || month > 12 || day < 1 || day > 31) return null
  if (year !== null) return new Date(year, month - 1, day)
  const thisYear = new Date(start.getFullYear(), month - 1, day)
  return thisYear >= start ? thisYear : new Date(start.getFullYear() + 1, month - 1, day)
}

/**
 * Das Ende vom Satz abtrennen. `end: undefined` — keins genannt; `null` für das Ganze —
 * eins genannt, aber nicht lesbar (dann lieber gar keine Regel als eine endlose).
 */
function splitEnd(text: string, start: Date | null): { text: string; end?: RecurrenceEnd } | null {
  const span = /^(.+?) (?:for|für) (\d+) ([a-zä]+)$/.exec(text)
  if (span) {
    const unit = UNITS[span[3]!]
    const n = Number(span[2])
    if (!unit || !start || n < 1) return null
    // „for 3 days“ ab dem 1. heißt 1., 2., 3. — der letzte Tag liegt eins vor Start + Dauer.
    const after = unit === 'day' ? addDays(start, n)
      : unit === 'week' ? addDays(start, n * 7)
      : unit === 'month' ? dateInMonth(start.getFullYear(), start.getMonth() + n, start.getDate())
      : dateInMonth(start.getFullYear() + n, start.getMonth(), start.getDate())
    return { text: span[1]!, end: { kind: 'date', date: dateKey(addDays(after, -1)) } }
  }
  const until = /^(.+?) (?:until|ending|ending on|bis|bis zum|bis am|endet|endet am) (.+)$/.exec(text)
  if (until) {
    const end = start ? endDateOf(until[2]!.replace(/^(?:on|am|zum) /, ''), start) : null
    return end ? { text: until[1]!, end: { kind: 'date', date: dateKey(end) } } : null
  }
  return { text }
}

function translateRhythm(text: string, afterDone: boolean, anchorDay: Date | null): RecurrenceRule | null {
  if (!afterDone && !/\b(hours?|until|ending|bis|endet)\b/.test(text)) {
    const ordinal = monthlyOrdinal(text, anchorOf(anchorDay))
    if (ordinal !== undefined) return ordinal
  }
  if (UNSUPPORTED.some(pattern => pattern.test(text))) return null
  const anchor = anchorOf(anchorDay)

  const quota = quotaOf(text)
  if (quota) return afterDone ? null : quota

  const standalone = STANDALONE[text]
  if (standalone === 'workdays') return afterDone ? null : { type: 'fixed', interval: 1, unit: 'week', weekdays: [...WORKDAYS] }
  if (standalone) return afterDone ? { type: 'after_done', interval: 1, unit: standalone } : fixedRule(standalone, 1, anchor)

  const lead = /^(every|jeden|jede|jedes|jeder|alle) (.+)$/.exec(text)
  if (!lead) return null
  let rest = lead[2]!

  // „every 27 jan“ — vor dem Abstand prüfen, sonst läse sich die 27 als „alle 27“.
  const yearly = monthDayOf(rest)
  if (yearly) return afterDone ? null : { type: 'fixed', interval: 1, unit: 'year', yearMonth: yearly.month, yearDay: yearly.day }

  // Abstand: „3 days“, „other week“, „zweiten Tag“, „zwei Wochen“.
  let interval = 1
  const count = /^(\d+|[a-zäöü]+) (.+)$/.exec(rest)
  if (count && (/^\d+$/.test(count[1]!) || NUMBER_WORDS[count[1]!] !== undefined)) {
    const n = /^\d+$/.test(count[1]!) ? Number(count[1]) : NUMBER_WORDS[count[1]!]!
    if (n < 1) return null
    interval = n
    rest = count[2]!
  }

  // „week(s) on mon, thu“ / „wochen am montag“
  const unitWithDays = /^([a-zä]+) (?:on the|on|am|an|jeweils am) (.+)$/.exec(rest)
  const unitWord = unitWithDays ? unitWithDays[1]! : rest
  const unit = UNITS[unitWord]
  if (unit) {
    if (afterDone) return unitWithDays ? null : { type: 'after_done', interval, unit }
    if (!unitWithDays) return fixedRule(unit, interval, anchor)
    const detail = unitWithDays[2]!
    if (unit === 'week') {
      const days = weekdaysOf(detail)
      return days ? { type: 'fixed', interval, unit: 'week', weekdays: days } : null
    }
    if (unit === 'month') {
      const day = /^(\d{1,2})(st|nd|rd|th|\.)?$/.exec(detail)
      const monthDay = day ? Number(day[1]) : NaN
      return monthDay >= 1 && monthDay <= 31 ? { type: 'fixed', interval, unit: 'month', monthDay } : null
    }
    return null
  }

  if (afterDone) return null // „every! monday“ kennt HappyTask nicht

  if (/^(weekday|weekdays|workday|workdays|werktag|werktage)$/.test(rest)) {
    return interval === 1 ? { type: 'fixed', interval: 1, unit: 'week', weekdays: [...WORKDAYS] } : null
  }
  if (/^(weekend|weekends|wochenende)$/.test(rest)) {
    return { type: 'fixed', interval, unit: 'week', weekdays: [...WEEKEND] }
  }
  const days = weekdaysOf(rest)
  if (days) return { type: 'fixed', interval, unit: 'week', weekdays: days }

  // „every 15th“ / „jeden 15.“ — Monatstag
  const monthDay = /^(\d{1,2})(st|nd|rd|th|\.)?$/.exec(rest)
  if (monthDay && interval === 1) {
    const day = Number(monthDay[1])
    return day >= 1 && day <= 31 ? { type: 'fixed', interval: 1, unit: 'month', monthDay: day } : null
  }

  return null
}
